import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin } from "lucide-react";
import { useLanguage } from "@/i18n";

const Guida = () => {
  const { t } = useLanguage();
  const g = t.guide;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div data-testid="guide-page" className="pt-28 pb-24 px-6 sm:px-12 lg:px-20 bg-[#F8F5F0]">
      <div className="max-w-5xl mx-auto">
        <Link
          data-testid="guide-back-link"
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C86D51] font-semibold hover:text-[#0D253A] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> {g.back}
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-8 max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#C86D51] font-semibold">{g.eyebrow}</p>
          <h1 data-testid="guide-title" className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#0D253A] mt-3 tracking-tight">
            {g.title}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#0D253A]/75 leading-relaxed">{g.intro}</p>
        </motion.header>

        {/* House rules */}
        <section data-testid="guide-rules" className="mt-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C86D51] font-semibold">{g.rules.label}</p>
          <h2 className="font-display text-2xl sm:text-3xl text-[#0D253A] mt-2">{g.rules.title}</h2>
          <ol className="mt-8 flex flex-col gap-4">
            {g.rules.items.map((item, i) => (
              <motion.li
                key={i}
                data-testid={`guide-rule-${i}`}
                initial={{ opacity: 0, x: -18 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex gap-4 items-start bg-white rounded-2xl border border-[#0D253A]/10 p-5"
              >
                <span className="shrink-0 w-8 h-8 rounded-full bg-[#0D253A] text-[#F8F5F0] flex items-center justify-center font-display text-sm">
                  {i + 1}
                </span>
                <p className="text-sm sm:text-base text-[#1A2129] leading-relaxed">{item}</p>
              </motion.li>
            ))}
          </ol>
        </section>

        {/* Practical info */}
        <section data-testid="guide-practical" className="mt-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C86D51] font-semibold">{g.practical.label}</p>
          <h2 className="font-display text-2xl sm:text-3xl text-[#0D253A] mt-2">{g.practical.title}</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {g.practical.items.map((item, i) => (
              <motion.div
                key={i}
                data-testid={`guide-practical-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="bg-white rounded-2xl border border-[#0D253A]/10 p-6"
              >
                <h3 className="font-display text-lg text-[#0D253A]">{item.title}</h3>
                <p className="mt-2 text-sm text-[#5C6773] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Area: map + tips */}
        <section data-testid="guide-area" className="mt-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C86D51] font-semibold">{g.area.label}</p>
          <h2 className="font-display text-2xl sm:text-3xl text-[#0D253A] mt-2">{g.area.title}</h2>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <div data-testid="guide-map" className="rounded-2xl overflow-hidden border border-[#0D253A]/10 shadow-[0_20px_50px_-30px_rgba(13,37,58,0.5)]">
              <iframe
                title={g.area.mapTitle}
                src="https://www.google.com/maps?q=Isola+delle+Femmine,+PA,+Italia&z=14&output=embed"
                className="w-full h-[360px] lg:h-[440px]"
                loading="lazy"
              />
            </div>

            <div className="flex flex-col gap-4">
              {g.area.tips.map((tip, i) => (
                <motion.div
                  key={i}
                  data-testid={`guide-tip-${i}`}
                  initial={{ opacity: 0, x: 18 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex gap-4"
                >
                  <span className="mt-1 shrink-0 p-2 h-fit rounded-full bg-[#C86D51]/12 text-[#C86D51]">
                    <MapPin className="w-4 h-4" />
                  </span>
                  <div>
                    <h3 className="font-display text-lg text-[#0D253A]">{tip.title}</h3>
                    <p className="mt-1 text-sm text-[#5C6773] leading-relaxed">{tip.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Guida;
